import React, { useState } from 'react';
import { FaBars, FaTimes } from 'react-icons/fa';

const LandingPageListItems = ({ 
  landingPages, 
  onView, 
  onCopy, 
  onEdit 
}) => {
  const [expandedPages, setExpandedPages] = useState(new Set());

  // Toggle mobile menu for a specific landing page
  const togglePageMenu = (pageId) => {
    setExpandedPages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pageId)) {
        newSet.delete(pageId);
      } else {
        newSet.add(pageId);
      }
      return newSet;
    });
  };

  return (
    <div className="space-y-3">
      {landingPages.map((landingPage) => {
        const isExpanded = expandedPages.has(landingPage._id);
        return (
          <div 
            key={landingPage._id}
            className="bg-black rounded-lg border border-gray-800 hover:border-gray-700 transition-all duration-200"
          >
            <div className="p-3 sm:p-4 lg:p-6">
              <div className="flex items-start lg:items-center justify-between">
                {/* Left Side - Main Info */}
                <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4 flex-1 min-w-0 pr-1 sm:pr-2">
                  {/* Landing Page Icon with Status Dot */}
                  <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12 relative">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12 bg-gray-800 rounded-lg sm:rounded-xl flex items-center justify-center relative">
                      {landingPage.projectId?.logo ? (
                        <img 
                          src={landingPage.projectId.logo.startsWith('http') ? landingPage.projectId.logo : `${window.location.origin}${landingPage.projectId.logo}`}
                          alt={landingPage.projectId.name}
                          className="w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12 rounded-lg sm:rounded-xl object-cover border border-gray-600"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) {
                              e.target.nextSibling.style.display = 'flex';
                            }
                          }}
                        />
                      ) : null}
                      <div 
                        className="w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12 bg-gray-800 rounded-lg sm:rounded-xl flex items-center justify-center absolute inset-0"
                        style={{ display: landingPage.projectId?.logo ? 'none' : 'flex' }}
                      >
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 lg:w-6 lg:h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                    </div>
                    {/* Status Indicator */}
                    <div className={`absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2 h-2 sm:w-3 sm:h-3 rounded-full border border-gray-900 sm:border-2 ${
                      landingPage.status === 'published' ? 'bg-green-400' : 
                      landingPage.status === 'draft' ? 'bg-yellow-400' : 'bg-gray-400'
                    }`}></div>
                  </div>

                  {/* Landing Page Info */}
                  <div className="flex flex-col space-y-0.5 sm:space-y-1 flex-1 min-w-0">
                    {/* Title */}
                    <div className="text-xs sm:text-sm lg:text-base text-white font-medium text-left truncate">
                      {landingPage.title}
                    </div>
                    
                    {/* URL */}
                    <div className="text-xs sm:text-sm text-left">
                      <span className="text-gray-400 text-xs">{window.location.origin}/landing/</span>
                      <span className="text-white font-bold text-xs">{landingPage.customUrl || landingPage.title?.toLowerCase().replace(/[^a-z0-9]/g, '-')}</span>
                    </div>
                    
                    {/* Project and Stats */}
                    <div className="flex flex-wrap items-center gap-1 sm:gap-2 lg:gap-3 text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                        <span className="truncate">{landingPage.projectId?.name || 'No Project'}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>{new Date(landingPage.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desktop Actions */}
                <div className="hidden lg:flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onView(landingPage);
                    }}
                    className="flex items-center px-3 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-gray-700 text-sm"
                  >
                    <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onCopy(landingPage);
                    }}
                    className="flex items-center px-3 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-gray-700 text-sm"
                  >
                    <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(landingPage);
                    }}
                    className="flex items-center px-3 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-gray-700 text-sm"
                  >
                    <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit
                  </button>
                </div>

                {/* Mobile hamburger menu */}
                <div className="lg:hidden flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => togglePageMenu(landingPage._id)}
                    className="p-2 text-white hover:text-gray-300 transition-colors bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600"
                  >
                    {isExpanded ? (
                      <FaTimes className="h-4 w-4" />
                    ) : (
                      <FaBars className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Mobile expanded menu */}
              {isExpanded && (
                <div className="lg:hidden mt-4 pt-4 border-t border-gray-600 bg-gray-800 rounded-lg p-3">
                  <div className="grid grid-cols-2 gap-2 lg:gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onView(landingPage);
                      }}
                      className="px-2 lg:px-3 py-2 bg-gray-700 text-white text-xs font-medium rounded hover:bg-gray-600 transition-colors flex items-center justify-center"
                    >
                      <svg className="mr-2 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onCopy(landingPage);
                      }}
                      className="px-2 lg:px-3 py-2 bg-gray-700 text-white text-xs font-medium rounded hover:bg-gray-600 transition-colors flex items-center justify-center"
                    >
                      <svg className="mr-2 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(landingPage);
                      }}
                      className="px-2 lg:px-3 py-2 bg-gray-700 text-white text-xs font-medium rounded hover:bg-gray-600 transition-colors flex items-center justify-center"
                    >
                      <svg className="mr-2 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default LandingPageListItems;